import { useEffect, useRef, useState } from 'react';
import { WALKTHROUGH_INTRO, WALKTHROUGH_MODAL_DELAY } from '../Model/Model';

/**
 * @Author      Karen Yunuen Pozos Arias
 * @JobTitle    Full Stack
 * @Email       kpozos@ibl.mx
 *
 * Resolves the card of the current step and the element it points to. Step 0 is the welcome card,
 * steps 1..n follow the steps of the permissions of the admin. A step that opens a modal waits for its animation
 * before looking for the target, and the card is centered when no target is in the screen.
 */
const WalkthroughApp = ({ step, steps }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    // The card is hidden while its target is being looked for, so it does not flash in the center //
    const [resolved, setResolved] = useState(false);
    // Instance of Popper, updated when the target moves //
    const popperRef = useRef(null);
    const isIntro = step === 0;
    const currentStep = isIntro ? WALKTHROUGH_INTRO : steps[step - 1];
    const totalSteps = steps.length;
    const isLastStep = step === totalSteps;

    useEffect(() => {
        setAnchorEl(null);
        setResolved(false);
        const findTarget = () => {
            const selector = (currentStep?.TARGETS ?? []).find((target) => document.querySelector(target));
            setAnchorEl(selector ? document.querySelector(selector) : null);
            setResolved(true);
        };
        // A step that opens or closes a modal waits for its animation: the closed modal gives back to the page
        // the width of the scrollbar, so the targets of the page move once it is gone //
        const previousStep = step > 1 ? steps[step - 2] : null;
        const timeout = setTimeout(findTarget, currentStep?.MODAL || previousStep?.MODAL ? WALKTHROUGH_MODAL_DELAY : 0);
        return () => clearTimeout(timeout);
    }, [step]);

    /**
     * Places the card again whenever its target moves without a scroll or a resize of the window: a centered
     * modal moves its buttons while it finishes its animation or its content changes its height, and the page
     * moves its targets when the padding of the scrollbar of a modal is added to or removed from the body.
     */
    useEffect(() => {
        if (!anchorEl) return;
        const update = () => popperRef.current?.update();
        const container = anchorEl.closest('.modal-dialog') ?? anchorEl;
        const observer = new ResizeObserver(update);
        observer.observe(container);
        observer.observe(document.body);
        container.addEventListener('transitionend', update);
        update();
        return () => {
            observer.disconnect();
            container.removeEventListener('transitionend', update);
        };
    }, [anchorEl]);

    return {
        anchorEl,
        popperRef,
        resolved,
        currentStep,
        totalSteps,
        isIntro,
        isLastStep,
        progressClassName: `event-monitoring-walkthrough-progress-fill h-100 border-radius-8px event-monitoring-walkthrough-progress-${step}-of-${totalSteps}`
    };
};

export default WalkthroughApp;
